import { armourIsPainted } from '../ui/armor-art';
/**
 * The ore ladder, client side — a MIRROR of backend crafting/ores.config.ts.
 *
 * The world is generated on the client from a seed (the server has no map), so
 * the client renders which vein stands where and therefore has to know the ore
 * table. It does NOT decide anything that costs: the vein's METAL is re-derived
 * server-side from the same seed + tile (veinVariantAt below, mirrored in backend
 * mining/vein.math.ts), and so are the pickaxe gate, the yield and the smelt
 * time — a tampered table buys nothing.
 *
 * Kept as a plain table rather than fetched at boot because world generation runs
 * before any request completes — a vein's identity is part of the terrain, not
 * part of the player's state.
 */

export const ORE_TYPES = ['copper', 'iron', 'gold', 'mythril', 'emerald', 'amber', 'ruby'] as const;
export type OreType = (typeof ORE_TYPES)[number];

export interface OreInfo {
  type: OreType;
  name: string;
  /** Pickaxe tier needed to break this vein (server-enforced; shown as the hint). */
  pickaxeTier: number;
  /** Share of veins on the map — common copper down to rare mythril. */
  weight: number;
  /** The metal's colour: minimap dot, panel accents, and the tint applied to
   *  weapon/armour art that has no per-tier sprite of its own. */
  color: string;
  /** Crafting tier this metal equips (3=Copper … 6=Mythril). */
  tier: number;
}

export const ORES: Record<OreType, OreInfo> = {
  copper:  { type: 'copper',  name: 'Copper',  pickaxeTier: 1, weight: 0.37, color: '#b86f50', tier: 3 },
  iron:    { type: 'iron',    name: 'Iron',    pickaxeTier: 3, weight: 0.265, color: '#c9d2dd', tier: 4 },
  gold:    { type: 'gold',    name: 'Gold',    pickaxeTier: 4, weight: 0.16, color: '#dd9a08', tier: 5 },
  mythril: { type: 'mythril', name: 'Mythril', pickaxeTier: 5, weight: 0.085, color: '#00cdf9', tier: 6 },
  // Tiers 7-9 — the three ore rows the pack drew and nothing used, as real metals
  // (2026-08-23). Mirror of backend crafting/ores.config.ts: pickaxeTier, tier and
  // WEIGHT must match exactly, or a vein renders as one metal and mines as another.
  emerald: { type: 'emerald', name: 'Emerald', pickaxeTier: 6, weight: 0.055, color: '#3fbf6f', tier: 7 },
  // Deep orange, NOT the pack row's honey (#f0921e).
  //
  // The pack's amber and gold rows sit 2.6 degrees apart in hue, so as 16px icons
  // they were the same picture — the owner read an amber bar as a gold one. The
  // art is remapped to this colour (scripts/build-ore-nodes.py RECOLOR) and this
  // value has to match it: the vein, the chunk, the bar, the minimap dot and the
  // tier tint on tier-8 gear all name the same orange or they disagree on screen.
  amber:   { type: 'amber',   name: 'Amber',   pickaxeTier: 7, weight: 0.035, color: '#d66b00', tier: 8 },
  ruby:    { type: 'ruby',    name: 'Ruby',    pickaxeTier: 8, weight: 0.03,  color: '#e04a5a', tier: 9 },
};

export const ORE_LIST: OreInfo[] = ORE_TYPES.map((t) => ORES[t]);

/**
 * A vein's ore is stored in `Decor.variant` as an index into ORE_TYPES, which is
 * why this is an ordered list and not a map lookup: the world snapshot is a
 * number per decor, and renaming or reordering these would repaint an existing
 * player's map with different metals.
 */
export const oreByVariant = (variant: number): OreInfo => ORE_LIST[variant] ?? ORES.copper;

/** Pick a vein's ore from the world RNG, weighted so copper is what you meet first. */
export function rollOreVariant(r: number): number {
  let acc = 0;
  for (let i = 0; i < ORE_LIST.length; i++) {
    acc += ORE_LIST[i].weight;
    if (r < acc) return i;
  }
  return 0;
}

/**
 * WHICH METAL STANDS WHERE — the mirror of backend mining/vein.math.ts.
 *
 * A vein's metal used to be a free roll of the world RNG, which meant only the
 * client knew it and the server had to believe the request body: `oreType:
 * "mythril"` minted mythril anywhere. Now the metal is a pure function of the
 * seed and the tile — the map is cut into VEIN_REGION-square regions, one metal
 * each — so the server recomputes it and a forged claim is simply refused.
 *
 * Keep these two functions IDENTICAL to the backend copy (same hash, same region
 * size, same weights) or a vein will render as one metal and mine as another.
 */
export const VEIN_REGION = 8;

function regionUnit(seed: number, rx: number, ry: number): number {
  let h = seed ^ 0x7f4a7c15;
  h = Math.imul(h ^ (rx + 0x9e3779b9), 16777619);
  h = Math.imul(h ^ (ry + 0x85ebca6b), 16777619);
  h ^= h >>> 13;
  return ((h >>> 0) % 100000) / 100000;
}

/** Index into ORE_LIST for the metal of the region containing (tx, ty). */
export function veinVariantAt(seed: number, tx: number, ty: number): number {
  const r = regionUnit(seed >>> 0, Math.floor(tx / VEIN_REGION), Math.floor(ty / VEIN_REGION));
  let acc = 0;
  for (let i = 0; i < ORE_LIST.length; i++) {
    acc += ORE_LIST[i].weight;
    if (r < acc) return i;
  }
  return 0;
}

/**
 * How far out a metal's field is allowed to spawn, as dist01 from the farm.
 *
 * The frontier survives the move to a seed-hashed metal as a PLACEMENT rule: a
 * region whose metal is too rare for how close it sits simply grows no cluster.
 * It cannot be a re-weighting any more, because the server has no farm position
 * to weight by and both sides must agree on the answer.
 *
 * These are fractions of the map's OWN reach — 0.72 means "in the outermost 28%
 * of the ground a vein could stand on", not "0.72 of the bounding box". World gen
 * normalises by the farthest ELIGIBLE tile for exactly that reason; normalising
 * by the rectangle's corner (what it used to do) made 1.0 unreachable, dist01
 * topped out at 0.74 on the shipped draft map, and mythril — which needs 0.72 —
 * placed ZERO veins on every seed, so the whole top rung of the ladder (mythril
 * ore, bars, tier-6 tools/weapons, tier-5 armour) could not be obtained at all.
 */
export const MIN_DIST01 = [0, 0.40, 0.58, 0.72,
  // Tiers 7-9 share mythril's frontier rather than being pushed past it. 0.72 is
  // already the outermost band a vein can occupy on the shipped draft map, and
  // pushing a metal beyond it is exactly how mythril once placed ZERO veins
  // map-wide. These are walled off by PICKAXE TIER instead (6/7/8), which is a
  // gate the player can see and work toward.
  0.72, 0.72, 0.72];

/** Map node / raw chunk / bar art, all sliced from the pack's Ores sheet. */
export const oreNodeSprite = (o: OreInfo) => `rock_${o.type}`;
export const oreChunkIcon = (o: OreInfo) => `/sprites/mining/chunk_${o.type}.png`;
export const oreBarIcon = (o: OreInfo) => `/sprites/mining/bar_${o.type}.png`;

/**
 * Tint for gear of a given crafting tier — the owner's explicit instruction for
 * the tiers with no dedicated weapon/armour sprite: "if we don't have sprites for
 * weapons, we need to use those colors". Wood and Stone keep their own art
 * untinted (0xffffff is Pixi's no-op tint / CSS pass-through).
 */
export const TIER_TINT: Record<number, string> = Object.fromEntries(
  ORE_LIST.map((o) => [o.tier, o.color]),
);
export const tierTint = (tier: number): string | null => TIER_TINT[tier] ?? null;

/**
 * Armour tier → the crafting tier whose METAL it is made of.
 *
 * The two ladders are off by one and always have been: tools run
 * [Wood, Stone, Copper, Iron, Gold, Mythril] from 1, armour has no wooden piece
 * and runs [Stone, Copper, Iron, Gold, Mythril] from 1. So armour tier 4 is Gold,
 * which is crafting tier 5. Getting this wrong silently paints a gold breastplate
 * in iron, which is the owner's "use correct color for each item".
 */
export const armourTintTier = (armourTier: number): number => armourTier + 1;

/** The metal an armour tier is made of, as a CSS colour (null = keep its own art). */
export const armourTint = (armourTier: number): string | null => tierTint(armourTintTier(armourTier));

/**
 * The colour to PAINT an armour layer with, or null to leave the art alone.
 *
 * The pack draws four plate metals for an eight-rung ladder, so the top three
 * tiers wear the plain Iron plate and the colour is the only thing telling emerald
 * from amber from ruby — or from Iron. Below that the metal is in the art already
 * and painting it would fight the pack's own shading.
 *
 * Lives here, next to the palette, because every renderer that dresses a hero
 * needs the same answer: the farm's paper-doll and the arena's fighter both go
 * through this rather than each deciding for itself. They already share one layer
 * table so the arena cannot show a different metal from the farm; the paint has to
 * follow the same route or the arena silently undoes it.
 */
/**
 * Stone armour's colour. Not from the ore table — stone is not an ore, so
 * `tierTint` has nothing for it — but sampled the same way the ore colours were:
 * the mean body tone of the pack's own `sprites/resources/stone.png`, outline and
 * highlight excluded.
 *
 * It exists because Stone had the SAME collision as the tiers above mythril, and
 * for the same reason. The pack has no stone plate, so PLATE_COLOUR sends tier 1
 * to Iron — and tier 3 IS Iron, so the first rung of the armour ladder and the
 * third were pixel-identical. A player crafting Iron armour saw nothing change.
 */
export const STONE_ARMOUR_TINT = '#a3a3ad';

export const armourPaintColor = (armourTier: number): string | null => {
  if (Math.floor(armourTier) === 1) return STONE_ARMOUR_TINT;
  return armourIsPainted(armourTier) ? armourTint(armourTier) : null;
};
/** The same as a Pixi tint number (0xRRGGBB), or 0xffffff for "leave it alone". */
export const tierTintNum = (tier: number): number => {
  const hex = TIER_TINT[tier];
  return hex ? parseInt(hex.slice(1), 16) : 0xffffff;
};
